/**
 * Who owns a roadmap row or a risk (D247, B-12): a person on the project, chosen from its members and shown by name.
 * Nobody outside the project can be picked, so an owner is always someone a record can be addressed to. The project
 * page's "Make someone the owner" picks the same way, leaving out the people who own it already.
 */

import type { OwnerRef } from '@aioe/shared';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { ChoiceSelect } from './ui.tsx';

const NONE = '__none';

/** The project's people (members who are persons), by id, for the picker, with their role on it. */
export function useProjectPeople(projectId: string) {
  const project = useLoad(() => routes.project(projectId), [projectId]);
  return (project.data?.members ?? []).filter((m) => m.subjectType === 'user').map((m) => ({ id: m.subjectId, name: m.name ?? m.email ?? m.subjectId, role: m.role }));
}

export function OwnerPicker({
  projectId,
  value,
  onChange,
  label,
  className,
  isDisabled,
  exclude,
  noneLabel = 'No owner',
}: {
  projectId: string;
  /** The owner's id, else their name. */
  value?: string;
  onChange: (owner: OwnerRef | undefined) => void;
  label: string;
  className?: string;
  isDisabled?: boolean;
  /** People not to offer (by id). */
  exclude?: (person: { id: string; role?: string }) => boolean;
  /** What the empty choice says. */
  noneLabel?: string;
}) {
  const people = useProjectPeople(projectId).filter((p) => !exclude?.(p));
  // An owner who is no longer on the project, or was named without an id, is still shown by name until another is chosen.
  const current = value && !people.some((p) => p.id === value || p.name === value) ? [{ id: value, name: value }] : [];
  const all = [...people, ...current];
  const options: Record<string, string> = { [NONE]: noneLabel, ...Object.fromEntries(all.map((p) => [p.id, p.name])) };
  const selected = all.find((p) => p.id === value || p.name === value)?.id ?? NONE;
  return (
    <ChoiceSelect
      label={label}
      className={className ?? 'w-full'}
      value={selected}
      isDisabled={isDisabled}
      options={options}
      onChange={(v) => onChange(v === NONE ? undefined : { id: v, name: options[v] ?? v })}
    />
  );
}
